import { ReactNode, CSSProperties } from 'react';
import styled from 'styled-components';
import { s16, zIndex } from '@ardoq/design-tokens';
import {
  EXPORT_AUTOSIZE_CLASS_NAME,
  VIEW_SCROLLBAR_WIDTH_CSS_VARIABLE,
  VISUALIZATION_LEGEND_CONTAINER_CLASS_NAME,
} from '@ardoq/global-consts';
import { DEFAULT_ARDOQ_I18NLOCALE, I18nLocale, t } from '@ardoq/i18n';

const BASE_HEIGHT_OFFSET = 16;
export const ViewLegendContainerElement = styled.div.attrs({
  className: `${EXPORT_AUTOSIZE_CLASS_NAME} ${VISUALIZATION_LEGEND_CONTAINER_CLASS_NAME}`,
})<{ $heightOffset: number; $visible: boolean | undefined }>`
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  position: absolute;
  max-width: 344px;
  min-width: 224px;
  z-index: ${zIndex.VIEW_LEGEND};
  max-height: calc(
    100% - ${({ $heightOffset }) => $heightOffset + BASE_HEIGHT_OFFSET}px
  );
  bottom: ${({ $heightOffset }) => $heightOffset + BASE_HEIGHT_OFFSET}px;
  right: 0px;
  margin-right: calc(${s16} + var(${VIEW_SCROLLBAR_WIDTH_CSS_VARIABLE}, 0px));
  margin-left: auto;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  user-select: text;

  &.isVisible {
    visibility: visible;
  }
  &.isHidden {
    visibility: hidden;
  }

  /* the LegendContainer is the .forExport element */
  &.forExport {
    max-height: unset;
    height: fit-content;
    width: fit-content;
    visibility: visible;
  }

  /* the LegendContainer is inside of a .forExport element */
  .forExport & {
    flex: unset;
    height: fit-content;
    max-height: unset;
    max-height: none;
    bottom: 0px;
    visibility: visible;
  }

  &&.hideLegendDuringExport {
    visibility: hidden;
  }
`;

export type ViewLegendContainerProps = {
  children?: ReactNode;
  heightOffset?: number;
  visible?: boolean;
  style?: CSSProperties;
  i18nLocale?: I18nLocale;
};

export const ViewLegendContainer = ({
  style,
  heightOffset = 0,
  visible,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...containerProps
}: ViewLegendContainerProps) => {
  return (
    <ViewLegendContainerElement
      {...containerProps}
      aria-label={t(
        i18nLocale,
        'graph:viewLegendContainer.ariaLabel',
        'Legend container'
      )}
      $heightOffset={heightOffset}
      $visible={visible}
      style={{ ...style, top: 'unset' }} // along with the .autoSize class, top: unset forces htmlExport to not set top to the computed value
    />
  );
};
